import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../services/auth.service';
import { AdministrationService } from '../../services/administration.service';
import { Credencial } from '../../models/User';
import { ScnackbarData, ToastService, SnackbarStyles } from '../../commons/toast.service';
import { HttpErrorResponse } from '@angular/common/http';

export function passwordMatchValidator(newPasswordKey: string, confirmPasswordKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const newPassword = control.get(newPasswordKey)?.value;
    const confirmPassword = control.get(confirmPasswordKey)?.value;
    if (newPassword === "" || confirmPassword == "") {
      return null
    }
    if (newPassword !== confirmPassword) {
      control.get(confirmPasswordKey)?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      return null;
    }
  };
}

// Validador para longitud mínima
export function minLengthValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return value.length >= minLength ? null : { minLength: { requiredLength: minLength, actualLength: value.length } };
  };
}

// Validador para mayúsculas
export function uppercaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[A-Z]/.test(value) ? null : { uppercase: true };
  };
}

// Validador para minúsculas
export function lowercaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[a-z]/.test(value) ? null : { lowercase: true };
  };
}

// Validador para números
export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[0-9]/.test(value) ? null : { number: true };
  };
}

// Validador para caracteres especiales
export function specialCharValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? null : { specialChar: true };
  };
}

// Validador para que la nueva contraseña no sea igual a la actual
export function passwordNotSameValidator(currentPasswordKey: string, newPasswordKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const currentPassword = control.get(currentPasswordKey)?.value;
    const newPassword = control.get(newPasswordKey)?.value;
    
    if (!currentPassword || !newPassword || currentPassword === '' || newPassword === '') {
      return null;
    }
    
    if (currentPassword === newPassword) {
      control.get(newPasswordKey)?.setErrors({ samePassword: true });
      return { samePassword: true };
    }
    
    return null;
  };
}

@Component({
  selector: 'app-dlg-force-password-change',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './dlg-force-password-change.component.html',
  styleUrl: './dlg-force-password-change.component.scss',
  changeDetection: ChangeDetectionStrategy.Default
})
export class DlgForcePasswordChangeComponent {
  hideActual = signal(true);
  hideNueva = signal(true);
  hideConfirmar = signal(true);

  fetchingUpdatePassword = false
  submitted = false
  incorrectPasswordError = false
  snackbarData: ScnackbarData = {}
  
  message: string = 'Tu contraseña ha caducado, por favor actualízala';
  title: string = 'CAMBIO DE CONTRASEÑA REQUERIDO';
  icon: string = 'lock_reset';
  isForced: boolean = false;
  readonly data = inject<any>(MAT_DIALOG_DATA);

  updatePasswordFormControl = new FormGroup({
    currentPassword: new FormControl("", [
      Validators.required
    ]),
    newPassword: new FormControl("", [
      Validators.required,
      minLengthValidator(10),
      uppercaseValidator(),
      lowercaseValidator(),
      numberValidator(),
      specialCharValidator()
    ]),
    confirmPassword: new FormControl("", [
      Validators.required,
    ])
  }, {
    validators: [
      passwordMatchValidator('newPassword', 'confirmPassword')
    ]
  })

  @ViewChild('currentPasswordInput') currentPasswordInput!: ElementRef<HTMLInputElement>;

  constructor(
    private authService: AuthService,
    private administrationService: AdministrationService,
    private toast: ToastService,
    public dialogRef: MatDialogRef<DlgForcePasswordChangeComponent>
  ) {
    // Evitar que el diálogo se cierre con Escape o clic fuera
    dialogRef.disableClose = true;
    
    if (this.data) {
      if (this.data.reason === 'expired') {
        this.message = 'Tu contraseña ha caducado, por favor actualízala';
        this.title = 'CONTRASEÑA EXPIRADA';
        this.icon = 'lock_clock';
        this.isForced = false;
      } else if (this.data.reason === 'forced') {
        this.message = 'Por razones de seguridad, debes cambiar tu contraseña inmediatamente';
        this.title = 'CAMBIO OBLIGATORIO POR SEGURIDAD';
        this.icon = 'gpp_bad';
        this.isForced = true;
      }
    }
  }

  toggleVisibility(signalVar: any, event: MouseEvent) {
    signalVar.set(!signalVar());
    event.stopPropagation();
  }

  onSubmitPasswordForm() {
    this.submitted = true;
    this.incorrectPasswordError = false;
    
    // Validar que la nueva contraseña no sea igual a la actual
    const currentPassword = this.updatePasswordFormControl.controls.currentPassword.value;
    const newPassword = this.updatePasswordFormControl.controls.newPassword.value;
    
    if (currentPassword === newPassword) {
      this.snackbarData.message = "La nueva contraseña no puede ser igual a la actual"
      this.snackbarData.style = SnackbarStyles.Error
      this.toast.show(this.snackbarData)
      return;
    }
    
    this.fetchingUpdatePassword = true
    const userData = this.authService.getTokenData()
    let credencial: Credencial = {
      id: parseInt(userData!.id),
      usuario: userData!.sub,
      contrasena: this.updatePasswordFormControl.controls.currentPassword.value!,
      nuevaContrasena: this.updatePasswordFormControl.controls.newPassword.value!
    }
    
    this.administrationService.PutCurrentUserPassword(credencial).subscribe({
      next: (response) => {
        this.fetchingUpdatePassword = false
        this.snackbarData.message = "Contraseña actualizada correctamente!"
        this.snackbarData.style = SnackbarStyles.Success
        this.toast.show(this.snackbarData)
        
        // Cerrar el diálogo y hacer logout para que vuelva a iniciar sesión
        this.dialogRef.close(true);
        setTimeout(() => {
          this.authService.logout();
        }, 1000);
      },
      error: (error: HttpErrorResponse) => {
        let errorMessage = "Error al actualizar la contraseña";
        
        if (error.status === 406) {
          const errorBody = error.error;
          if (errorBody === 'old-mismatch') {
            errorMessage = "La contraseña actual es incorrecta";
            this.incorrectPasswordError = true;
          } else if (errorBody === 'same-password') {
            errorMessage = "La nueva contraseña no puede ser igual a la actual";
          } else if (errorBody === 'weak-password') {
            errorMessage = "La contraseña es muy débil. Debe ser más segura";
          } else {
            errorMessage = "Verifica los datos ingresados";
          }
        }
        
        this.snackbarData.message = errorMessage
        this.snackbarData.style = SnackbarStyles.Error
        this.fetchingUpdatePassword = false
        this.toast.show(this.snackbarData)
        
        if (this.currentPasswordInput) {
          this.currentPasswordInput.nativeElement.focus();
        }
      }
    })
  }
}
